import uniqBy from "lodash/uniqBy";
import { ChatBody } from "PFApp/components/chat/chat_body";
import { ChatSubheader } from "PFApp/components/chat/chat_subheader";
import { showChatProfile } from "PFApp/components/chat/show_chat_profile";
import { useProfilePreview } from "PFApp/use_profile_preview";
import { LoadingDots } from "PFComponents/loading_dots";
import { ProfileAvatar } from "PFComponents/profile_avatar/profile_avatar";
import { getProfileName } from "PFCore/helpers/profile";
import { useChatParticipants } from "PFCore/hooks/queries/chat/use_chat_participants";
import { Profile } from "PFCore/types";
import { ChatParticipant } from "PFTypes/chat_participant";
import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import css from "./chat_participants.module.scss";

type ChatParticipantsProps = {
  conversationId: number;
  ownerId: number;
  withMessage?: boolean;
  handleClose: VoidFunction;
  handleMinimize: VoidFunction;
  isPrivate: boolean;
};

export const ChatParticipants = ({
  conversationId,
  ownerId,
  withMessage,
  handleClose,
  handleMinimize,
  isPrivate
}: ChatParticipantsProps) => {
  const { t } = useTranslation(["translation", "core"]);
  const { showPreview } = useProfilePreview();

  const { data: participants = [], isLoading: participantsLoading } = useChatParticipants({
    conversationId,
    withMessage
  });

  const { data: ownerParticipants = [], isLoading: ownerLoading } = useChatParticipants({
    conversationId,
    profileId: ownerId,
    perPage: 1
  });

  const allParticipants: ChatParticipant[] =
    ownerParticipants.length > 0
      ? uniqBy([ownerParticipants[0], ...participants], "profile.id")
      : participants;

  const isLoading = participantsLoading || ownerLoading;
  const firstLoading = isLoading && allParticipants.length === 0;

  const isOwner = (profile, id: number) => profile.id === id;
  const isViewable = (profile) => profile.viewable === true;

  const profileFullName = (profile, id: number) => (
    <React.Fragment>
      <span>{getProfileName(profile)}</span>
      {isOwner(profile, id) ? <small className={css.owner}>({t("translation:chat.owner")})</small> : null}
    </React.Fragment>
  );

  return (
    <React.Fragment>
      <ChatSubheader
        handleClose={handleClose}
        title={isPrivate ? t("core:chat.type.private") : t("core:chat.type.public")}
        center
      />
      {firstLoading && <LoadingDots centered />}
      {!firstLoading && (
        <ChatBody opacity={isLoading ? 0.5 : 1}>
          <ul className={css.list} data-qa-id="chat-participants">
            {allParticipants.map((participant) => {
              const { profile } = participant;
              const avatar = (
                <div className={css.avatarWrap}>
                  <ProfileAvatar profile={profile} size={32} />
                </div>
              );
              const name = <div className={css.name}>{profileFullName(profile, ownerId)}</div>;

              if (isViewable(profile) || isOwner(profile, ownerId)) {
                return (
                  <li className={css.item} key={participant.id}>
                    <Link
                      to={`/profiles/${profile.id}`}
                      onClick={(event) => {
                        event.preventDefault();
                        showChatProfile(handleMinimize, event, profile as Profile, showPreview);
                      }}
                      className={css.link}
                    >
                      {avatar} {name}
                    </Link>
                  </li>
                );
              } else {
                return (
                  <li className={css.item} key={participant.id}>
                    {avatar} {name}
                  </li>
                );
              }
            })}
          </ul>
        </ChatBody>
      )}
    </React.Fragment>
  );
};
